import type { ElementRef, MouseEvent } from 'react';
import { useEffect, useRef, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { getRouteApi } from '@tanstack/react-router';
import jwt_decode from 'jwt-decode';
import type { SubmitErrorHandler, SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { z } from 'zod';

import Finished from '../components/Finished';
import type { SchemaJWT } from '../schemas/jwt';
import { schemaJWT } from '../schemas/jwt';
import type { SchemaCode } from '../schemas/login';
import { schemaCode } from '../schemas/login';
import type { SchemaResponse2FA } from '../schemas/response2FA';
import { schemaResponse2FA } from '../schemas/response2FA';
import { APICommunicator } from '~/api/APICommunicator';
import NotFound from '~/components/error/NotFound';
import TreatButton from '~/components/TreatButton';

export default function ResetSecretRoute() {
    const { t } = useTranslation();
    const secretRef = useRef<ElementRef<'div'>>(null);
    const [finished, setFinished] = useState(false);
    const [response2FA, setResponse2FA] = useState<SchemaResponse2FA | null>(null);
    const { useSearch } = getRouteApi('/_auth/reset/secret/');
    const { jwt } = useSearch();

    if (typeof jwt !== 'string' || jwt === '') {
        throw new Error('JWT not found');
    }

    const { handleSubmit, register } = useForm<SchemaCode>({
        defaultValues: {
            code: '',
        },
        resolver: zodResolver(schemaCode),
    });

    const onSubmit: SubmitHandler<SchemaCode> = async ({ code }) => {
        const response = await APICommunicator.resetResource({
            config: {
                body: JSON.stringify({ code }),
                method: 'POST',
            },
            path: '/reset/secret',
            schema: z.object({
                success: z.boolean(),
            }),
        });
        if (response.success) {
            setFinished(true);
            return;
        }

        toast.error(response.error, { theme: 'colored' });
    };

    const onError: SubmitErrorHandler<SchemaCode> = (formError) => {
        if (formError.code) {
            toast.error(t('LOGIN.CODE_NOT_VALID'), { theme: 'colored' });
        }
    };

    useEffect(() => {
        if (response2FA == null) {
            APICommunicator.resetResource({
                config: {
                    method: 'GET',
                },
                path: '/reset/secret',
                schema: schemaResponse2FA,
            }).then((response) => {
                if (response.success) {
                    setResponse2FA(response.data);
                } else {
                    toast.error(response.error, { theme: 'colored' });
                }
            });
        }
    }, [response2FA]);

    let decodedJwt: SchemaJWT;
    try {
        decodedJwt = schemaJWT.parse(jwt_decode(jwt));
    } catch {
        return <NotFound />;
    }

    if (!decodedJwt.id || decodedJwt.type !== '2fa' || decodedJwt.exp * 1000 < Date.now() || !response2FA)
        return <NotFound />;

    if (finished) return <Finished>{t('users.reset.secret.FINISH_INFO')}</Finished>;

    const copyToClipboard = (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        if (secretRef.current) {
            const textToCopy = secretRef.current.textContent || '';
            navigator.clipboard
                .writeText(textToCopy)
                .then(() => {
                    toast.info(t('LOGIN.CODE_COPIED_TO_CLIPBOARD'), { theme: 'colored' });
                })
                .catch(() => {
                    toast.error(t('LOGIN.COPY_CODE_TO_CLIPBOARD_FAILED'), {
                        theme: 'colored',
                    });
                });
        }
    };

    return (
        <>
            {response2FA ? (
                <div className="flex items-center justify-center w-full min-h-screen p-4">
                    <div className="w-full max-w-[820px] p-4 mx-auto bg-grey-light">
                        <h3 className="mb-4 text-3xl font-bold text-center text-blue">{t('PAGES.LOGIN')}</h3>
                        <form onSubmit={handleSubmit(onSubmit, onError)}>
                            <div
                                dangerouslySetInnerHTML={{ __html: response2FA.qr }}
                                style={{ height: '500px', margin: 'auto', width: '500px' }}
                            />
                            <div className="flex justify-between">
                                <div ref={secretRef}>{response2FA.secret}</div>
                                <button
                                    onClick={copyToClipboard}
                                    className="text-xs font-semibold bg-blue py-2 px-3.5 text-white"
                                >
                                    {t('LOGIN.CODE_COPY')}
                                </button>
                            </div>
                            <input
                                {...register('code')}
                                type="text"
                                className="bg-white form-input border-transparent w-full placeholder:text-dark text-grey text-xs px-3 py-2.5 mb-4"
                                placeholder={t('LOGIN.PLACEHOLDER_CODE')}
                                aria-label={t('LOGIN.2FA_CODE_LABEL')}
                                required
                            />
                            <div className="flex items-center justify-end space-x-5">
                                <TreatButton className="text-xs font-semibold bg-blue py-2 px-3.5 text-white">
                                    {t('users.reset.secret.SUBMIT')}
                                </TreatButton>
                            </div>
                        </form>
                    </div>
                </div>
            ) : (
                t('LOGIN.SECRET_IS_BRING_GENERATED')
            )}
        </>
    );
}
